<template>
  <button
    type="button"
    class="vst-button inline-flex justify-center items-center gap-2 rounded-md font-semibold focus:outline-none text-sm transition-all"
    :class="{
      ...buttonData.params,
      [size === 'mini'
        ? 'py-1 px-3'
        : size === 'large'
          ? 'py-3 px-7'
          : 'py-1.5 px-5']: true,
      [disabled
        ? 'disabled opacity-50 focus:ring-0 focus:ring-offset-0 cursor-not-allowed'
        : 'focus:ring-2 focus:ring-offset-2']: true,
      ['!px-2 !py-2 !rounded-full']: circle,
      ['!px-2 !py-2 !rounded-md']: square,
    }"
    @click="handleClick"
  >
    <slot />
    <i
      v-if="icon"
      :class="'ti ' + icon"
      class="pointer-events-none"
    />
    <div
      v-if="loading"
      role="status"
    >
      <svg
        aria-hidden="true"
        class="inline w-4 h-4 text-gray-200 animate-spin fill-blue-600"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    </div>
  </button>
</template>
<script>
export default {
  props: {
    type: { type: String, default: () => '' },
    color: { type: String, default: 'blue' },
    size: { type: String, default: () => '' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    icon: { type: String, default: () => '' },
    circle: Boolean,
    square: Boolean,
  },
  data() {
    return {
      buttonClass: {},
    };
  },
  computed: {
    buttonData() {
      return this.buttonClass?.[this.type] || this.buttonClass.default;
    },
  },
  watch: {
    color: {
      immediate: true,
      handler(newColor) {
        this.buttonClass = {
          primary: {
            params: {
              'border-1 border-transparent text-white': true,
              [`bg-${newColor}-500`]: true,
              [`hover:bg-${newColor}-600`]: true,
              [`focus:ring-${newColor}-500`]: true,
            },
          },
          text: {
            params: {
              'border-1 border-transparent': true,
              [`text-${newColor}-500`]: true,
              [`hover:bg-${newColor}-100`]: true,
              [`focus:ring-${newColor}-500`]: true,
            },
          },
          plain: {
            params: {
              'border border-1 border-transparent hover:text-white ring-offset-white': true,
              [`bg-${newColor}-100`]: true,
              [`text-${newColor}-500`]: true,
              [`hover:bg-${newColor}-500`]: true,
              [`focus:ring-${newColor}-500`]: true,
            },
          },
          border: {
            params: {
              'border border-1 font-medium bg-white shadow-sm align-middle hover:bg-gray-50 focus:ring-offset-white': true,
              [`text-${newColor}-700`]: true,
              [`focus:ring-${newColor}-600`]: true,
            },
          },
          link: {
            params: {
              'border border-1 border-transparent ring-offset-white': true,
              [`text-${newColor}-600`]: true,
              [`hover:text-${newColor}-700`]: true,
              [`focus:ring-${newColor}-600`]: true,
            },
          },
          default: {
            params: {
              'border-1 border-gray-200 hover:text-white bg-white': true,
              [`text-${newColor}-500`]: true,
              [`border-${newColor}-200`]: true,
              [`hover:border-${newColor}-500`]: true,
              [`hover:bg-${newColor}-500`]: true,
              [`focus:ring-${newColor}-500`]: true,
            },
          },
        };
      },
    },
  },
  methods: {
    handleClick($event) {
      if (!this.disabled) {
        this.$emit('click', $event);
      }
    },
  },
};
</script>
<style lang="scss">
.vst-button {
  width: fit-content;
  &:active {
    opacity: 0.8;
  }

  &.disabled {
    &:active {
      opacity: 0.5;
    }
  }
}
</style>
